import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { API, auth } from '../finance/shared';
import { statusMeta } from './bookingStatusMeta';

const WEEKDAYS = ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'];
const MONTH_NAMES = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
const toIso = (d) => d.toISOString().slice(0, 10);
const ACTIVE_STATUSES = ['pending', 'pending_payment', 'pending_payment_verification', 'booked'];

export default function MonthCalendarView({ venueId, onSelectDay }) {
  const [anchor, setAnchor] = useState(new Date());
  const [byDay, setByDay] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    try {
      const res = await axios.get(`${API}/venues/${venueId}/bookings`, { ...auth(), params: { date_from: toIso(first), date_to: toIso(last) } });
      const grouped = {};
      (res.data || []).forEach((b) => {
        if (!grouped[b.date]) grouped[b.date] = [];
        grouped[b.date].push(b);
      });
      setByDay(grouped);
    } catch { setByDay({}); } finally { setLoading(false); }
  }, [venueId, anchor]);

  useEffect(() => { load(); }, [load]);

  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const daysInMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
  const leadingBlanks = first.getDay();
  const cells = [...Array(leadingBlanks).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const todayIso = toIso(new Date());

  return (
    <div data-testid="month-calendar-view">
      <div className="mb-3 flex items-center justify-between">
        <button onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))} data-testid="month-calendar-prev" className="rounded-lg border border-slate-200 p-1.5 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
        <p className="text-sm font-bold text-slate-800">{MONTH_NAMES[anchor.getMonth()]} {anchor.getFullYear()}</p>
        <button onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))} data-testid="month-calendar-next" className="rounded-lg border border-slate-200 p-1.5 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold text-slate-400">
        {WEEKDAYS.map((w) => <div key={w} className="py-1">{w}</div>)}
      </div>
      <div className={`grid grid-cols-7 gap-1 ${loading ? 'opacity-50' : ''}`} data-testid="month-calendar-grid">
        {cells.map((day, idx) => {
          if (!day) return <div key={idx} />;
          const dateStr = toIso(new Date(anchor.getFullYear(), anchor.getMonth(), day));
          const dayBookings = byDay[dateStr] || [];
          const activeCount = dayBookings.filter((b) => ACTIVE_STATUSES.includes(b.status)).length;
          const statuses = [...new Set(dayBookings.map((b) => b.status))].slice(0, 3);
          const isToday = dateStr === todayIso;
          return (
            <button
              key={idx} onClick={() => onSelectDay(dateStr)} data-testid={`month-calendar-day-${dateStr}`}
              className={`flex min-h-[64px] flex-col items-center gap-1 rounded-lg border p-1.5 text-xs transition-colors hover:border-teal-400 ${isToday ? 'border-teal-400 bg-teal-50' : 'border-slate-200'}`}
            >
              <span className={`font-bold ${isToday ? 'text-teal-700' : 'text-slate-700'}`}>{day}</span>
              {activeCount > 0 && <span className="rounded-full bg-slate-800 px-1.5 text-[10px] font-bold text-white">{activeCount}</span>}
              {statuses.length > 0 && (
                <div className="flex gap-0.5">
                  {statuses.map((s) => <span key={s} className={`h-1.5 w-1.5 rounded-full ${statusMeta(s).dot}`} />)}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
